import React, { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import logo from '../logo.png';
import { requestPasswordReset } from '../services/userService';

export default function ForgotPasswordScreen() {
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || '');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!email) {
      setError('Email is required');
      return;
    }

    try {
      setIsSubmitting(true);
      const result = await requestPasswordReset({ email });
      setSuccessMessage(
        result?.message || 'If an account with that email exists, a password reset link has been sent.'
      );
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container
      maxWidth="sm"
      sx={{
        minHeight: '100vh',
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
      }}
    >
      <Paper elevation={4} sx={{ width: '100%', p: { xs: 3, sm: 4 } }}>
        <Stack spacing={3} alignItems="center" textAlign="center">
          <Box
            component="img"
            src={logo}
            alt="Logo"
            sx={{
              width: 128,
              height: 128,
              borderRadius: '50%',
              bgcolor: 'common.white',
              p: 1,
            }}
          />

          <MailOutlineRoundedIcon color="primary" sx={{ fontSize: 64 }} />

          <Stack spacing={1}>
            <Typography variant="h5" component="h1" fontWeight={700}>
              Forgot your password?
            </Typography>
            <Typography color="text.secondary">
              Enter your email and we&apos;ll send you a reset link if your account exists.
            </Typography>
          </Stack>

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <Stack spacing={2}>
              {error && <Alert severity="error">{error}</Alert>}
              {successMessage && (
                <Alert severity="success" sx={{ textAlign: 'left' }}>
                  {successMessage}
                </Alert>
              )}
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                required
                fullWidth
              />
              <Button type="submit" variant="contained" size="large" fullWidth disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send reset link'}
              </Button>
            </Stack>
          </Box>

          <Button component={RouterLink} to="/" variant="text">
            Back to sign in
          </Button>
        </Stack>
      </Paper>
    </Container>
  );
}
